import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { bulkCreateStudents, type CreatedStudent } from '../../lib/api';
import { connectGoogle, googleConfigured, listCourses, listStudents, type GCourse } from '../../lib/googleClassroom';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// Add a whole class at once: from Google Classroom, or a pasted list of names.
// Each name becomes a username account in the active class, with a generated
// password the teacher hands out (student-accounts → bulk_create). The
// passwords are shown once, here; any can be reset from the roster later.
// ─────────────────────────────────────────────────────────────────────────────

type Row = { name: string; on: boolean; dupe: boolean };
const MAX = 60;

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

export default function AddStudents() {
  const { hydrated, active, students, refresh } = useClassroom();
  const [source, setSource] = useState<'google' | 'paste'>(googleConfigured() ? 'google' : 'paste');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [paste, setPaste] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [courses, setCourses] = useState<GCourse[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: CreatedStudent[]; failed: { displayName: string; reason: string }[] } | null>(null);
  const [copied, setCopied] = useState(false);

  const onRoster = useMemo(() => new Set(students.map(s => norm(s.display_name))), [students]);

  if (!hydrated) return <Loading />;
  if (!active) return <div className="notice">Create a class first. <Link to="/roster">Roster</Link></div>;

  // Names already on the roster start unticked: most likely the same student.
  const review = (names: string[]) => {
    const seen = new Set<string>();
    setRows(names.map(n => n.replace(/\s+/g, ' ').trim()).filter(n => n.length > 0 && n.length <= 60)
      .filter(n => { const k = norm(n); if (seen.has(k)) return false; seen.add(k); return true; })
      .map(n => ({ name: n, dupe: onRoster.has(norm(n)), on: !onRoster.has(norm(n)) })));
  };

  const run = async (label: string, f: () => Promise<void>) => {
    setBusy(label); setError(null);
    try { await f(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  const google = () => run('Connecting to Google…', async () => {
    const t = await connectGoogle();
    setToken(t);
    setCourses(await listCourses(t));
  });
  const pickCourse = (c: GCourse) => run(`Reading ${c.name}…`, async () => {
    const names = await listStudents(token!, c.id);
    if (names.length === 0) throw new Error(`${c.name} has no students yet.`);
    review(names);
  });

  const chosen = rows?.filter(r => r.on) ?? [];
  const create = () => run(`Creating ${chosen.length} account${chosen.length === 1 ? '' : 's'}…`, async () => {
    if (chosen.length > MAX) throw new Error(`Add at most ${MAX} students at a time. Untick some and do the rest after.`);
    const r = await bulkCreateStudents(active.id, chosen.map(c => c.name));
    setResult(r);
    await refresh();
  });

  const loginsText = (list: CreatedStudent[]) =>
    `${active.name}: sign in at ${window.location.host}\n\n` +
    list.map(c => `${c.displayName}\tusername: ${c.username}\tpassword: ${c.password}`).join('\n');
  const copy = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(loginsText(result.created)); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copy these logins:', loginsText(result.created)); }
  };

  // ── 3. Done: the logins to hand out ──────────────────────────────────────
  if (result) {
    return (
      <div className="narrow">
        <div className="crumbs no-print"><Link to="/roster">← Roster</Link></div>
        <h1>Logins for {active.name}</h1>
        <p className="sub no-print">
          {result.created.length} account{result.created.length === 1 ? '' : 's'} made and added to the class.
          <strong> Copy or print these now: the passwords aren’t shown again.</strong> You can reset any of them from the roster.
        </p>
        {result.failed.length > 0 && (
          <div className="notice bad small no-print" style={{ marginBottom: 14 }}>
            Not added: {result.failed.map(f => `${f.displayName} (${f.reason})`).join('; ')}
          </div>
        )}
        <div className="row no-print" style={{ marginBottom: 14 }}>
          <button onClick={copy}>{copied ? 'Copied ✓' : 'Copy all'}</button>
          <button onClick={() => window.print()}>Print login cards</button>
        </div>
        <p className="small print-only">Sign in at <strong>{window.location.host}</strong></p>
        <div className="login-cards">
          {result.created.map(c => (
            <div key={c.username} className="login-card">
              <div className="small muted">{active.name}</div>
              <strong>{c.displayName}</strong>
              <div className="mono">Username: {c.username}</div>
              <div className="mono">Password: {c.password}</div>
              <div className="small muted">{window.location.host}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="narrow">
      <div className="crumbs"><Link to="/roster">← Roster</Link></div>
      <h1>Add students</h1>
      <p className="sub">
        Make an account for each student in <strong>{active.name}</strong> at once. You’ll get a username and
        password for each to hand out. Students can still join with the class code too.
      </p>

      {/* ── 1. Where the names come from ── */}
      {!rows && (
        <div className="panel">
          <div className="seg" style={{ marginBottom: 16 }}>
            <button type="button" className={source === 'google' ? 'on' : ''} onClick={() => setSource('google')}>Google Classroom</button>
            <button type="button" className={source === 'paste' ? 'on' : ''} onClick={() => setSource('paste')}>Paste a list</button>
          </div>

          {source === 'google' ? (
            !googleConfigured() ? (
              <div className="notice small">Google Classroom import isn’t set up yet. Paste the names instead for now.</div>
            ) : !courses ? (
              <>
                <p className="small" style={{ marginTop: 0 }}>
                  Sign in with the Google account you use for Google Classroom. We only read your class lists
                  (names, never emails or grades), and nothing is kept from Google.
                </p>
                <button className="primary" onClick={google} disabled={!!busy}>{busy ?? 'Connect Google Classroom'}</button>
              </>
            ) : courses.length === 0 ? (
              <div className="notice small">That Google account doesn’t teach any active classes. <button className="small" onClick={google}>Use another account</button></div>
            ) : (
              <>
                <p className="small" style={{ marginTop: 0 }}>Which Google class?</p>
                <div className="list">
                  {courses.map(c => (
                    <button key={c.id} className="item link" style={{ textAlign: 'left' }} onClick={() => pickCourse(c)} disabled={!!busy}>
                      <span className="grow"><span className="term">{c.name}</span>{c.section && <span className="gloss small">{c.section}</span>}</span>
                    </button>
                  ))}
                </div>
                {busy && <p className="small muted">{busy}</p>}
              </>
            )
          ) : (
            <>
              <div className="field">
                <label htmlFor="names">One name per line</label>
                <textarea id="names" rows={10} value={paste} onChange={e => setPaste(e.target.value)}
                  placeholder={'Ana Souza\nBruno Lima\nCarla Mendes'} />
                <span className="hint">Paste straight from a spreadsheet column or your school’s class list.</span>
              </div>
              <button className="primary" disabled={!paste.trim()} onClick={() => review(paste.split(/\r?\n|\t|;/))}>Next</button>
            </>
          )}
          {error && <div className="notice bad small" style={{ marginTop: 14 }}>{error}</div>}
        </div>
      )}

      {/* ── 2. Check the names ── */}
      {rows && (
        <div className="panel">
          <div className="row" style={{ marginBottom: 10 }}>
            <strong>{chosen.length} of {rows.length} selected</strong>
            <span className="spacer" />
            <button className="small" onClick={() => setRows(rows.map(r => ({ ...r, on: true })))}>All</button>
            <button className="small" onClick={() => setRows(rows.map(r => ({ ...r, on: false })))}>None</button>
          </div>
          {rows.some(r => r.dupe) && (
            <p className="small muted" style={{ marginTop: 0 }}>Names already on your roster are unticked, so nobody gets two accounts.</p>
          )}
          <div className="list">
            {rows.map((r, i) => (
              <label key={i} className="item check-line" style={{ margin: 0 }}>
                <input type="checkbox" checked={r.on} onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
                <input value={r.name} maxLength={60} aria-label="Student name" style={{ flex: 1 }}
                  onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                {r.dupe && <span className="pill muted">On roster</span>}
              </label>
            ))}
          </div>
          {chosen.length > MAX && <div className="notice bad small" style={{ marginTop: 12 }}>At most {MAX} at a time.</div>}
          {error && <div className="notice bad small" style={{ marginTop: 12 }}>{error}</div>}
          <div className="row" style={{ marginTop: 16 }}>
            <button className="primary" onClick={create} disabled={!!busy || chosen.length === 0 || chosen.length > MAX || chosen.some(c => !c.name.trim())}>
              {busy ?? `Create ${chosen.length} account${chosen.length === 1 ? '' : 's'}`}
            </button>
            <button onClick={() => { setRows(null); setError(null); }} disabled={!!busy}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}
